import envConfig from '@hulu/env-config';

import {
  ANALYTICS_EVENTS,
  ANALYTICS_PROPERTY_KEYS,
  AUTH_STATES,
  COMPONENT_OUTCOMES,
  SLOW_EXIT_MAX_TIME_ON_PAGE_MS,
} from '../constants';
import type { AnalyticsEventProperties, PageSessionState } from '../types';
import type { AnalyticsEventMap } from '../types/eventMap';
import { getPageFromPath, isSlowMetric } from '../utils';
import type { AnalyticsUserIdentity } from '../utils/resolveAnalyticsUserIdentity';
import { createAmplitudeAdapter } from './amplitudeAdapter';
import type { AnalyticsAdapter } from './analyticsAdapter';
import {
  clearAnalyticsSessionContext,
  getAnalyticsSessionContextProperties,
  setAnalyticsSelectedAdAccountId,
  setAnalyticsSessionContext,
  syncAnalyticsContextFromPathname,
} from './analyticsContext';

let adapter: AnalyticsAdapter = createAmplitudeAdapter();
let initialized = false;
let trackingEnabled = false;
let pendingUserIdentity: AnalyticsUserIdentity | null = null;
let currentPageSession: PageSessionState | null = null;

const applyUserIdentity = (identity: AnalyticsUserIdentity): void => {
  setAnalyticsSessionContext({ authState: AUTH_STATES.AUTHENTICATED });
  adapter.setUserId(identity.userId);

  if (identity.email) {
    adapter.setUserProperties({ email: identity.email });
  }
};

const getBaseProperties = (): AnalyticsEventProperties => ({
  [ANALYTICS_PROPERTY_KEYS.RELEASE_ID]: envConfig.REACT_APP_VERSION,
  [ANALYTICS_PROPERTY_KEYS.ENVIRONMENT]: envConfig.NODE_ENV,
  ...getAnalyticsSessionContextProperties(),
});

export {
  clearAnalyticsSessionContext,
  setAnalyticsSelectedAdAccountId,
  setAnalyticsSessionContext,
  syncAnalyticsContextFromPathname,
};

const initAnalytics = (): void => {
  const apiKey = envConfig.REACT_APP_AMPLITUDE_API_KEY;

  if (initialized || !trackingEnabled || !apiKey) {
    return;
  }

  adapter.init(apiKey, {
    defaultTracking: false,
    autocapture: false,
  });

  initialized = true;

  if (pendingUserIdentity) {
    applyUserIdentity(pendingUserIdentity);
  }
};

const shutdownAnalytics = (): void => {
  currentPageSession = null;

  if (!initialized) {
    return;
  }

  adapter.reset();
  initialized = false;
};

export const setAnalyticsAdapter = (nextAdapter: AnalyticsAdapter): void => {
  if (initialized) {
    shutdownAnalytics();
  }

  adapter = nextAdapter;
};

export const setAmplitudeTrackingEnabled = (enabled: boolean): void => {
  if (enabled === trackingEnabled) {
    if (enabled) {
      initAnalytics();
    }
    return;
  }

  trackingEnabled = enabled;

  if (enabled) {
    initAnalytics();
    return;
  }

  shutdownAnalytics();
};

export const trackAnalyticsEvent = <K extends keyof AnalyticsEventMap>(
  eventName: K,
  properties?: AnalyticsEventMap[K]
): void => {
  if (!initialized || !trackingEnabled) {
    return;
  }

  adapter.track(eventName, {
    ...getBaseProperties(),
    ...properties,
  });
};

export const identifyAnalyticsUser = (identity: AnalyticsUserIdentity | string): void => {
  const resolvedIdentity: AnalyticsUserIdentity = typeof identity === 'string' ? { userId: identity } : identity;

  if (!resolvedIdentity.userId) {
    return;
  }

  pendingUserIdentity = resolvedIdentity;

  if (!initialized || !trackingEnabled) {
    return;
  }

  applyUserIdentity(resolvedIdentity);
};

export const setAnalyticsUserProperties = (properties: Record<string, string | number | boolean>): void => {
  if (!initialized || !trackingEnabled) {
    return;
  }

  adapter.setUserProperties(properties);
};

export const resetAnalyticsUser = (): void => {
  pendingUserIdentity = null;

  if (!initialized) {
    return;
  }

  setAnalyticsSessionContext({ authState: AUTH_STATES.ANONYMOUS });
  adapter.reset();
};

export const resetAnalyticsClientStateForTests = (): void => {
  shutdownAnalytics();
  pendingUserIdentity = null;
  trackingEnabled = false;
  clearAnalyticsSessionContext();
};

export const updatePageSessionFromWebVital = (metricName: string, metricValue: number, pagePath: string): void => {
  if (!trackingEnabled) {
    return;
  }

  const isSlow = isSlowMetric(metricName, metricValue);

  if (currentPageSession && currentPageSession.path === pagePath) {
    if (metricName === 'LCP') {
      currentPageSession.lcpMs = metricValue;
    }
    currentPageSession.isSlow = currentPageSession.isSlow || isSlow;
  }
};

export const beginPageSession = (pathname: string): void => {
  if (!trackingEnabled) {
    return;
  }

  syncAnalyticsContextFromPathname(pathname);

  currentPageSession = {
    path: pathname,
    page: getPageFromPath(pathname),
    enteredAtMs: Date.now(),
    isSlow: false,
  };

  trackAnalyticsEvent(ANALYTICS_EVENTS.PAGE_VIEWED, {
    [ANALYTICS_PROPERTY_KEYS.PAGE_PATH]: currentPageSession.path,
    [ANALYTICS_PROPERTY_KEYS.PAGE]: currentPageSession.page,
    [ANALYTICS_PROPERTY_KEYS.COMPONENT_OUTCOME]: COMPONENT_OUTCOMES.AUTO,
  });
};

export const endPageSession = (): void => {
  if (!currentPageSession) {
    return;
  }

  const timeOnPageMs = Date.now() - currentPageSession.enteredAtMs;
  const isSlowExit = currentPageSession.isSlow && timeOnPageMs <= SLOW_EXIT_MAX_TIME_ON_PAGE_MS;

  trackAnalyticsEvent(ANALYTICS_EVENTS.PAGE_EXITED, {
    [ANALYTICS_PROPERTY_KEYS.PAGE_PATH]: currentPageSession.path,
    [ANALYTICS_PROPERTY_KEYS.PAGE]: currentPageSession.page,
    [ANALYTICS_PROPERTY_KEYS.TIME_ON_PAGE_MS]: timeOnPageMs,
    [ANALYTICS_PROPERTY_KEYS.IS_SLOW]: currentPageSession.isSlow,
    [ANALYTICS_PROPERTY_KEYS.COMPONENT_OUTCOME]: COMPONENT_OUTCOMES.AUTO,
    ...(isSlowExit && currentPageSession.lcpMs !== undefined
      ? { [ANALYTICS_PROPERTY_KEYS.METRIC_VALUE]: currentPageSession.lcpMs }
      : {}),
  });

  currentPageSession = null;
};

export const getCurrentPagePath = (): string | undefined => currentPageSession?.path;
